import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Typography,
} from "@mui/material";
import Breadcrumb from "../../components/common/breadcrumb/Breadcrumb";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { useSelector } from "react-redux";
import {
  addBulkUsers,
  getUserList,
  updateUserStatus,
} from "../../app/features/user/userSlice";
import getAxiosError from "../../app/api/axiosError";
import { toast } from "react-toastify";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { UserResponse } from "../../app/models/responseModels/userResponse";
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import CommandOpsDataGrid from "../../components/common/commandOpsDataGrid/CommandOpsDataGrid";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import CachedIcon from "@mui/icons-material/Cached";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import { PAGES } from "../../enums/PagesEnum";
import {
  DownloadFileModel,
  convertArrayToCSV,
  downloadCsv,
  downloadXlsx,
  prepareAndDownloadFile,
} from "../../helpers/downloadFile";
import {
  USER_COLUMN_NAME,
  USER_LABEL_NAME,
  UserExpectedColumns,
} from "./UserConstants";
import UserRolesTooltip from "./UserRolesTooltip";
import UserParcelCenterTooltip from "./UserParcelCenterTooltip";
import UserDataFilter from "./UserDataFilter";
import AddUserModal from "./modals/AddUserModal";
import {
  useSubmitModal,
  useSubmitModalWithParameter,
} from "../../components/common/modalPopup/useSubmitModal";
import EditUserModal from "./modals/EditUserModal";
import * as XLSX from "xlsx";
import UserOneTimePasswordButton from "./UserOneTimePasswordButton";
import FileUploadModal from "../../components/common/modalPopup/FileUploadModal";
import { BulkUserRequest } from "../../app/models/requestModels/userRequest";
import AddIcon from "@mui/icons-material/Add";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import FdpAdminDetails from "./FdpAdminDetails";
import { formatISODateString } from "../../components/common/utils/DateUtils";

function BulkUserAddRequestModel(users: any[]): BulkUserRequest {
  return {
    users: users,
  };
}

const breadcrumb = ["User"];
const User = () => {
  const dispatch = useAppDispatch();
  const { isLoading, userList } = useSelector((state: RootState) => state.user);

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.USER
    )
  );
  const isFdp = useSelector(
    (state: RootState) => state.login?.authResponse?.isFdp
  );
  const fdp = useSelector(
    (state: RootState) => state.login?.authResponse?.fdpDetails
  );

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        ) ||
        state.login?.authResponse?.userSettings?.roles.includes("FDP_ADMIN")
      : false
  );

  const IsCreatePermission = pagePermission
    ? pagePermission.isCreatePermission
    : false;

  // ***** States *****
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [filteredData, setFilteredData] = useState<UserResponse[]>([]);
  const [activeUsers, setActiveUsers] = useState<UserResponse[]>([]);
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [isTableLoading, setTableLoading] = useState<boolean>(false);
  const [isActiveUserView, setActiveUserView] = useState<boolean>(true);

  // ***** Callbacks *****
  const getUserData = useCallback(async () => {
    setTableLoading(true);
    try {
      const response = await dispatch(getUserList()).unwrap();
      if (response.isSuccess) {
        setTableLoading(false);
      }
      if (!response.isSuccess) {
        setTableLoading(false);
        toast.error(response.message!, {
          toastId: "userList",
        });
      }
    } catch (error) {
      setTableLoading(false);
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "userListException",
      });
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getUserData();
    }

    return () => {
      isFunctionRan = false;
    };
  }, [getUserData]);

  useEffect(() => {
    if (userList.length > 0) {
      const activeUsers = userList.filter((user) => user.deleted === "false");
      setActiveUsers(activeUsers);
    }
  }, [userList]);

  useEffect(() => {
    if (userList.length > 0) {
      if (isActiveUserView && filteredData.length === activeUsers.length) {
        setFilteredData(activeUsers);
      } else if (!isActiveUserView && filteredData.length === userList.length) {
        setFilteredData(userList);
      }
    }
  }, [activeUsers, userList, filteredData, isActiveUserView]);

  useEffect(() => {
    if (
      selectedRows.length > 0 &&
      selectedRows.some(
        (row: any) => row.original.deleted !== selectedRows[0].original.deleted
      )
    ) {
      toast.info("Either select Active or Inactive Rows!", {
        toastId: "mixedSelectionException",
      });
    }
  }, [selectedRows]);

  // ***** UseMemos *****
  // Columns
  const columns = useMemo<MRT_ColumnDef<UserResponse>[]>(
    () => [
      {
        accessorKey: USER_COLUMN_NAME.USER_ID,
        header: USER_LABEL_NAME.USER_ID,
        size: 100,
      },
      {
        accessorKey: USER_COLUMN_NAME.USER_NAME,
        header: USER_LABEL_NAME.USER_NAME,
        size: 100,
      },
      {
        accessorKey: USER_COLUMN_NAME.FIRSTNAME,
        header: USER_LABEL_NAME.FIRSTNAME,
        size: 100,
      },
      {
        accessorKey: USER_COLUMN_NAME.LASTNAME,
        header: USER_LABEL_NAME.LASTNAME,
        size: 100,
      },
      {
        accessorKey: USER_COLUMN_NAME.EMAIL,
        header: USER_LABEL_NAME.EMAIL,
        size: 150,
      },
      {
        accessorKey: USER_COLUMN_NAME.USER_DOCS,
        header: USER_LABEL_NAME.USER_DOCS,
        size: 50,
        Cell: ({ row }) => {
          const userDocs = row.original.userRelatedDocs;
          return userDocs && userDocs.length > 0 ? (
            <>
              {userDocs.map((doc) => (
                <Box
                  key={doc.id}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Typography sx={{ minWidth: "45px" }}>
                    {doc.fileType}
                  </Typography>
                  {doc.isUploaded ? (
                    <DoneIcon color="primary" />
                  ) : (
                    <CloseIcon color="error" />
                  )}
                </Box>
              ))}
            </>
          ) : (
            <span>N/A</span>
          );
        },
      },
      {
        accessorKey: USER_COLUMN_NAME.PHONE,
        header: USER_LABEL_NAME.PHONE,
        size: 150,
      },
      {
        accessorKey: USER_COLUMN_NAME.DELETED,
        header: USER_LABEL_NAME.DELETED,
        size: 50,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.deleted === "true" ? (
              <span>False</span>
            ) : (
              <span>True</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: USER_COLUMN_NAME.ASSIGNED_ROLES,
        header: USER_LABEL_NAME.ASSIGNED_ROLES,
        Cell: ({ cell, row }) => {
          const assignedRoles = row.original.assignedRoles;
          return assignedRoles && assignedRoles.length > 0 ? (
            <div>
              <UserRolesTooltip
                roles={assignedRoles.map((x) => x.roleName)}
              ></UserRolesTooltip>
            </div>
          ) : (
            <span>N/A</span>
          );
        },
        size: 50,
        enablePinning: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: USER_COLUMN_NAME.ASSIGNED_PARCEL_CENTERS,
        header: USER_LABEL_NAME.ASSIGNED_PARCEL_CENTERS,
        Cell: ({ cell, row }) => {
          const assignedPcs = row.original.assignedParcelCenters;
          return assignedPcs && assignedPcs.length > 0 ? (
            <div>
              <UserParcelCenterTooltip
                parcelCenters={row.original.assignedParcelCenters.map(
                  (x) => x.parcelCenterName
                )}
              ></UserParcelCenterTooltip>
            </div>
          ) : (
            <span>N/A</span>
          );
        },
        size: 20,
        enablePinning: false,
        enableColumnFilter: false,
      },
      {
        header: USER_LABEL_NAME.ONE_TIME_PASSWORD,
        Cell: ({ row }) => {
          return (
            <div
              style={{ display: "flex", justifyContent: "center" }}
              onClick={(e) => e.stopPropagation()}
            >
              <UserOneTimePasswordButton userInfo={row.original} />
            </div>
          );
        },
        size: 10,
        enablePinning: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: USER_COLUMN_NAME.CREATED_ON,
        header: USER_LABEL_NAME.CREATED_ON,
        size: 80,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
      {
        accessorKey: USER_COLUMN_NAME.UPDATED_ON,
        header: USER_LABEL_NAME.UPDATED_ON,
        size: 200,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
    ],
    []
  );

  const disableActivateButton = (table: MRT_TableInstance<UserResponse>) => {
    const selectedRows = table.getSelectedRowModel().flatRows;
    if (selectedRows.length === 0) {
      return true;
    }
    for (const row of selectedRows) {
      if (row.original.deleted === "false") {
        return true;
      }
    }
    return false;
  };

  const disableDeactivateButton = (table: MRT_TableInstance<UserResponse>) => {
    const selectedRows = table.getSelectedRowModel().flatRows;
    if (selectedRows.length === 0) {
      return true;
    }
    for (const row of selectedRows) {
      if (row.original.deleted === "true") {
        return true;
      }
    }
    return false;
  };
  // ***** Handlers *****
  const handleRefresh = () => {
    getUserData();
  };

  const handleResetAndRefresh = (table: MRT_TableInstance<UserResponse>) => {
    table.resetRowSelection(true);
    handleRefresh();
  };

  const handleDeleteStatus = (
    table: MRT_TableInstance<UserResponse>,
    deleteStatus: boolean
  ) => {
    let selectedRowIds: string[] = [];
    table.getSelectedRowModel().flatRows.map((row) => {
      selectedRowIds.push(row.original.id);
    });
    updateDeleteStatus(selectedRowIds, deleteStatus, table);
  };
  // ***** Handlers *****
  const updateDeleteStatus = async (
    selectedRowIds: string[],
    deleteStatus: boolean,
    table: MRT_TableInstance<UserResponse>
  ) => {
    try {
      const response = await dispatch(
        updateUserStatus({
          userIds: selectedRowIds,
          deleteStatus: deleteStatus,
        })
      ).unwrap();
      if (response.isSuccess) {
        handleResetAndRefresh(table);
        toast.success(response.message!, {
          toastId: "UpdateUsersStatusSuccess",
        });
      } else {
        toast.error(response.message!, {
          toastId: "UpdateUsersStatusError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "UpdateUsersStatusException",
      });
    }
    return true;
  };

  const handleBulkUsersImport = async (
    validData: any[],
    invalidData: any[]
  ) => {
    try {
      const invalidFileData = invalidData.map((item) => ({
        ...item.data,
        error: item.error, // Add the 'Error' field to each item
      }));
      if (validData.length === 0) {
        prepareAndDownloadFile(invalidFileData, "errorUserCreation_res");
        return false;
      }
      const bulkUserData = BulkUserAddRequestModel(validData);
      let fdpId = isFdp ? fdp?.id : null;
      const response = await dispatch(
        addBulkUsers({ bulkUserRequest: bulkUserData, fdpId: fdpId as number })
      ).unwrap();
      if (response.isSuccess) {
        if (invalidData.length) {
          prepareAndDownloadFile(invalidFileData, "errorUserCreation_res");
          toast.error("Some FDPs couldn't save, Please check the file!", {
            toastId: "AddBulkFdpException",
          });
          return true;
        }
        toast.success(response.message!, {
          toastId: "BulkUserImportSuccess",
        });
      } else {
        if (response.data && response.data.length) {
          const requiredKeys = UserExpectedColumns.map(
            (item) => item.camelCaseName
          );
          requiredKeys.push("error");
          // Filter response data to keep only keys present in invalidFileData
          const filteredResponseData = response.data.map((item: any) => {
            const filteredItem: any = {};
            requiredKeys.forEach((key) => {
              if (item.hasOwnProperty(key)) {
                filteredItem[key] = item[key] ? item[key].toLocaleString() : "";
              }
            });
            return filteredItem;
          });
          // Combine invalidFileData and filteredResponseData
          const mergedData = [...invalidFileData, ...filteredResponseData];
          prepareAndDownloadFile(mergedData, "errorUserCreation_res");
          toast.warning(response.message!, {
            toastId: "AddBulkFdpException",
          });
          return true;
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "BulkUserImportSuccess",
      });
    }
    return true;
  };

  const handleDownloadMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCsvFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      // Transform filteredData into CSV format
      const csvData = getFilteredDataFileMapping();
      // Convert CSV data to CSV string
      const csvContent = convertArrayToCSV(csvData);
      // Download the CSV
      const file: DownloadFileModel = {
        data: csvContent,
        contentType: "text/csv",
        fileName: "UserData.csv",
      };
      downloadCsv(file);
    } catch (error) {
      toast.error("unable to download file", {
        toastId: "userDownloadCsv",
      });
    }
  };

  const handleXlsxFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const fileData = getFilteredDataFileMapping();
      // Convert data to worksheet
      const worksheet = XLSX.utils.json_to_sheet(fileData);
      // Create a workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "UserData");
      // Convert the workbook to a binary XLSX object
      const wbArray = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      // Download the XLSX file
      downloadXlsx(blob, "UserData.xlsx");
    } catch (error) {
      toast.error("unable to download file", {
        toastId: "userDownloadXlsx",
      });
    }
  };

  const getFilteredDataFileMapping = () => {
    return filteredData.map((item: UserResponse) => {
      const parcelCenters = item.assignedParcelCenters
        ? `"${item.assignedParcelCenters
            .map((parcel) => parcel.parcelCenterName)
            .join(", ")}"`
        : "N/A";
      const roles = item.assignedRoles
        ? `"${item.assignedRoles.map((role) => role.roleName).join(", ")}"`
        : "N/A";
      const activeStatus = item.deleted === "true" ? "False" : "True";
      return {
        UserName: item.userName,
        FirstName: item.firstName,
        LastName: item.lastName,
        Email: item.email,
        Phone: item.phone,
        ActiveStatus: activeStatus,
        Roles: roles,
        ParcelCenters: parcelCenters,
        CreatedOn: item.createdOn,
        UpdatedOn: item.updatedOn,
      };
    });
  };

  // ***** Modal Popups *****
  const [editUserModal, openEditUserModal] =
    useSubmitModalWithParameter<UserResponse>(
      (userRowData, closeEditUserModal) => (
        <EditUserModal
          closeEditUserModal={closeEditUserModal}
          onCompleted={getUserData}
          userRowData={userRowData}
          isForFdps={isFdp}
        />
      )
    );

  const [addUserModal, openAddUserModal] = useSubmitModal(
    (closeAddUserModal) => (
      <AddUserModal
        closeAddUserModal={closeAddUserModal}
        onCompleted={getUserData}
        isForFdps={isFdp}
      />
    )
  );

  const [fileUploadModal, openFileUploadModal] = useSubmitModal(
    (closeFileUploadModal) => (
      <FileUploadModal
        closeFileUploadModal={closeFileUploadModal}
        expectedColumns={UserExpectedColumns}
        asyncSubmitOperation={handleBulkUsersImport}
        onCompleted={handleRefresh}
        sampleFileName={"Bulk_User_upload_Sample.xlsx"}
      />
    )
  );

  // ***** Datagrid Custom Actions *****
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<UserResponse>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={() => handleResetAndRefresh(table)}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Upload/Import">
          <IconButton type="button" onClick={openFileUploadModal}>
            <UploadIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Download/Export">
          <span>
            <IconButton
              disabled={filteredData.length === 0}
              type="button"
              onClick={handleDownloadMenuClick}
            >
              <DownloadIcon sx={{ color: "primary.main" }} />
            </IconButton>
          </span>
        </CustomTooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={(event) => handleCsvFileDownload(event)}>
            CSV
          </MenuItem>
          <MenuItem onClick={(event) => handleXlsxFileDownload(event)}>
            EXCEL
          </MenuItem>
        </Menu>
        {IsCreatePermission && IsAdmin && (
          <CustomTooltip arrow title="New User">
            <span>
              <IconButton
                disabled={isLoading}
                type="button"
                onClick={openAddUserModal}
              >
                <AddIcon sx={{ color: "primary.main" }} />
              </IconButton>
            </span>
          </CustomTooltip>
        )}
        {IsCreatePermission && IsAdmin && (
          <Button
            color="success"
            disabled={disableActivateButton(table)}
            onClick={() => handleDeleteStatus(table, false)}
            variant="contained"
            size="small"
            sx={{ ml: 1 }}
          >
            ACTIVATE
          </Button>
        )}
        {IsCreatePermission && IsAdmin && (
          <Button
            color="warning"
            disabled={disableDeactivateButton(table)}
            onClick={() => handleDeleteStatus(table, true)}
            variant="contained"
            size="small"
            sx={{ ml: 1 }}
          >
            DEACTIVATE
          </Button>
        )}
        {IsCreatePermission && IsAdmin && (
          <FormControlLabel
            title="Switch Users"
            control={
              <Switch
                size="small"
                checked={isActiveUserView}
                onChange={() => setActiveUserView(!isActiveUserView)}
              />
            }
            label={isActiveUserView ? "Active User" : ""}
            sx={{ ml: 1 }}
            labelPlacement="end"
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumb pageNameList={breadcrumb} />
      {isFdp && <FdpAdminDetails />}
      <>
        <UserDataFilter
          userData={isActiveUserView ? activeUsers : userList}
          setFilteredData={setFilteredData}
          filteredData={filteredData}
        />
        <Divider />
        <br />
      </>
      <CommandOpsDataGrid
        data={filteredData}
        columns={columns}
        isLoading={isLoading || isTableLoading}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        emptyRowMessage={"No users"}
        rowCount={filteredData.length.toLocaleString()}
        isDescending={false}
        defaultSortColumnName="userName"
        openModal={openEditUserModal}
        checkBoxSelection={true}
        setSelectedRows={setSelectedRows}
      />
      {addUserModal}
      {editUserModal}
      {fileUploadModal}
    </Box>
  );
};

export default User;
